/**
 * 69.59, a helicopter voiced (the rules and its two styles are `helicopterMix.ts`): one looping voice a helicopter, alive
 * while it is heard, let go after. Synthesized (no files), the autocannon's pulse trick for the blades:
 *   - **the rotor:** a sawtooth at the blade-pass rate through two wave-shapers, each a pulse a cycle — a broad one opening
 *     low noise and a low tone (the thwop), a sharp one opening a bright band (the slap, its level the slap's share);
 *   - **the tail rotor:** a sawtooth at its rate through a low-pass (the buzz);
 *   - **the turbines:** two triangles at the whine's pitch (a little apart for twin engines) through a narrow band;
 *   - **the wash:** noise under 1.3 kHz (a roar and the grit thrown up), slowly swelling and easing, its level the wash's.
 * Everything through the air's low-pass and the side. Cost: 5 oscillators, 3 noise sources, 2 wave-shapers, ~24 nodes
 * while it is heard; a frame, ~12 parameter updates. Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import { noiseBuf } from './gunfire';
import type { HeliHeard } from './helicopterMix';

export interface HeliOut { ctx: BaseAudioContext; dest: AudioNode }

const curves = new Map<string, Float32Array<ArrayBuffer>>();
/** A blade's pulse from the rate's sawtooth (−1 … 1, rising): full as each cycle begins, falling as (1 − x)^k. */
function pulse(k: number): Float32Array<ArrayBuffer> {
  const key = String(k);
  let c = curves.get(key);
  if (!c) {
    const n = 1024; c = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) { const x = i / (n - 1); let y = 1; for (let j = 0; j < k; j++) y *= 1 - x; c[i] = y; }
    curves.set(key, c);
  }
  return c;
}

interface Voice {
  out: GainNode; air: BiquadFilterNode; side: StereoPannerNode;
  rate: OscillatorNode; bodyLevel: GainNode; thwopTone: OscillatorNode; slapLevel: GainNode;
  tail: OscillatorNode; tailGain: GainNode;
  t1: OscillatorNode; t2: OscillatorNode; band: BiquadFilterNode; turbineGain: GainNode;
  washGain: GainNode;
  sources: AudioScheduledSourceNode[];
}

export class HelicopterVoice {
  private v: Voice | null = null;
  /** × its loudness (a host's volume). */
  volume = 1;
  constructor(private readonly out?: HeliOut) {}

  get live(): boolean { return this.v !== null; }

  /** This frame's heard values (null: silent — it fades and is let go). */
  update(h: HeliHeard | null): void {
    const where = this.where();
    if (!where) return;
    const now = where.ctx.currentTime;
    if (!h) { if (this.v) { this.release(this.v, now); this.v = null; } return; }
    const v = (this.v ??= this.voice(where.ctx, where.dest));
    v.rate.frequency.setTargetAtTime(h.rotor.rate, now, 0.1);
    v.thwopTone.frequency.setTargetAtTime(h.rotor.rate * 6, now, 0.1);
    v.bodyLevel.gain.setTargetAtTime(h.rotor.gain * h.rotor.body, now, 0.1);
    v.slapLevel.gain.setTargetAtTime(h.rotor.gain * h.rotor.slap * h.rotor.slap * 2.4, now, 0.15);
    v.tail.frequency.setTargetAtTime(h.tail.hz, now, 0.1);
    v.tailGain.gain.setTargetAtTime(h.tail.gain, now, 0.1);
    v.t1.frequency.setTargetAtTime(h.turbine.hz, now, 0.2);
    v.t2.frequency.setTargetAtTime(h.turbine.hz * (1 + h.turbine.spread), now, 0.2);
    v.band.frequency.setTargetAtTime(h.turbine.hz, now, 0.2);
    v.turbineGain.gain.setTargetAtTime(h.turbine.gain, now, 0.2);
    v.washGain.gain.setTargetAtTime(h.wash.gain, now, 0.3);
    v.air.frequency.setTargetAtTime(h.cutoff, now, 0.2);
    v.side.pan.setTargetAtTime(h.pan, now, 0.1);
    v.out.gain.setTargetAtTime(this.volume, now, 0.1);
  }

  stop(): void { this.update(null); }

  private where(): HeliOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }

  private release(v: Voice, t: number): void {
    v.out.gain.setTargetAtTime(0, t, 0.4);
    for (const s of v.sources) { try { s.stop(t + 2.5); } catch { /* already stopped */ } }
  }

  private voice(ctx: BaseAudioContext, dest: AudioNode): Voice {
    const sources: AudioScheduledSourceNode[] = [];
    const gain = (x: number) => { const g = ctx.createGain(); g.gain.value = x; return g; };
    const filter = (type: BiquadFilterType, hz: number, q: number) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = hz; f.Q.value = q; return f; };
    const osc = (type: OscillatorType, hz: number) => { const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz; sources.push(o); return o; };
    const noise = () => { const s = ctx.createBufferSource(); s.buffer = noiseBuf(ctx); s.loop = true; s.loopStart = Math.random(); sources.push(s); return s; };
    const out = gain(0), air = filter('lowpass', 9000, 0.5), side = ctx.createStereoPanner();
    air.connect(side); side.connect(out); out.connect(dest);
    // the rotor: the blade-pass sawtooth through a broad pulse (the thwop) and a sharp one (the slap)
    const rate = osc('sawtooth', 15);
    const broad = ctx.createWaveShaper(); broad.curve = pulse(3); rate.connect(broad);
    const sharp = ctx.createWaveShaper(); sharp.curve = pulse(16); rate.connect(sharp);
    const bodySrc = noise(), bodyLow = filter('lowpass', 240, 0.9), bodyVca = gain(0), bodyLevel = gain(0);
    broad.connect(bodyVca.gain); bodySrc.connect(bodyLow); bodyLow.connect(bodyVca); bodyVca.connect(bodyLevel); bodyLevel.connect(air);
    const thwopTone = osc('sine', 90), toneVca = gain(0), toneLevel = gain(0.9);
    broad.connect(toneVca.gain); thwopTone.connect(toneVca); toneVca.connect(toneLevel); toneLevel.connect(bodyLevel);
    const slapSrc = noise(), slapBand = filter('bandpass', 1100, 0.7), slapVca = gain(0), slapLevel = gain(0);
    sharp.connect(slapVca.gain); slapSrc.connect(slapBand); slapBand.connect(slapVca); slapVca.connect(slapLevel); slapLevel.connect(air);
    // the tail rotor's buzz
    const tail = osc('sawtooth', 70), tailLow = filter('lowpass', 420, 0.8), tailGain = gain(0);
    tail.connect(tailLow); tailLow.connect(tailGain); tailGain.connect(air);
    // the turbines' whine
    const t1 = osc('triangle', 5000), t2 = osc('triangle', 5000), band = filter('bandpass', 5000, 6), turbineGain = gain(0);
    t1.connect(band); t2.connect(band); band.connect(turbineGain); turbineGain.connect(air);
    // the wash: low noise, slowly swelling
    const washSrc = noise(), washLow = filter('lowpass', 1300, 0.5), washSwell = gain(0.75), washGain = gain(0);
    const lfo = osc('sine', 0.7), lfoDepth = gain(0.25);
    lfo.connect(lfoDepth); lfoDepth.connect(washSwell.gain);
    washSrc.connect(washLow); washLow.connect(washSwell); washSwell.connect(washGain); washGain.connect(air);
    for (const s of sources) s.start();
    return { out, air, side, rate, bodyLevel, thwopTone, slapLevel, tail, tailGain, t1, t2, band, turbineGain, washGain, sources };
  }
}
